
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface VisaType {
  id: string;
  name_ar: string;
  name_en: string;
  created_at: string;
  updated_at: string;
}

interface VisaTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visaType?: VisaType | null;
  onClose: () => void;
}

export function VisaTypeDialog({ open, onOpenChange, visaType, onClose }: VisaTypeDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    if (visaType) {
      setFormData({
        name_ar: visaType.name_ar,
        name_en: visaType.name_en,
      });
    } else {
      setFormData({
        name_ar: '',
        name_en: '',
      });
    }
  }, [visaType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (visaType) {
        // تحديث نوع تأشيرة موجود
        const { error } = await supabase
          .from('visa_types')
          .update({
            name_ar: formData.name_ar,
            name_en: formData.name_en,
            updated_at: new Date().toISOString(),
          })
          .eq('id', visaType.id);

        if (error) throw error;

        toast({
          title: "تم التحديث",
          description: "تم تحديث نوع التأشيرة بنجاح",
        });
      } else {
        // إضافة نوع تأشيرة جديد
        const { error } = await supabase
          .from('visa_types')
          .insert([{
            name_ar: formData.name_ar,
            name_en: formData.name_en,
          }]);

        if (error) throw error;

        toast({
          title: "تم الإضافة",
          description: "تم إضافة نوع التأشيرة بنجاح",
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving visa type:', error);
      toast({
        title: "خطأ",
        description: "فشل في حفظ نوع التأشيرة",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {visaType ? 'تعديل نوع التأشيرة' : 'إضافة نوع تأشيرة جديد'}
          </DialogTitle>
          <DialogDescription>
            {visaType 
              ? 'قم بتعديل بيانات نوع التأشيرة' 
              : 'أدخل بيانات نوع التأشيرة الجديد'
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name_ar" required>الاسم بالعربية</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                placeholder="اسم نوع التأشيرة بالعربية"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name_en" required>الاسم بالإنجليزية</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="Visa Type Name in English"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'جاري الحفظ...' : (visaType ? 'تحديث' : 'إضافة')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
